import { CEFR_LEVELS, type CefrLevel } from "@byfluent/domain";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router";
import { LineChart, ScoreBar } from "../../components/Charts";
import {
  Alert,
  Badge,
  Button,
  Card,
  Chips,
  Empty,
  Loading,
  Modal,
  PageHeader,
  SelectInput,
  Tabs,
  TextArea,
  TextInput,
} from "../../components/ui";
import { api, errorMessage } from "../../lib/api";
import { dateBR, dateTimeBR, score } from "../../lib/format";
import type {
  FullReport,
  Quota,
  Scenario,
  SessionSummary,
  SimulationLinkSummary,
  TeacherStudentItem,
} from "../../lib/types";
import { type ScenarioDraft, ScenarioEditor } from "./ScenarioEditor";

type Tab = "scenarios" | "simulations" | "evolution" | "notes";

const LINK_STATUS: Record<
  SimulationLinkSummary["status"],
  { label: string; color: "blue" | "violet" | "green" | "gray" | "red" }
> = {
  sent: { label: "Enviado", color: "blue" },
  opened: { label: "Aberto", color: "violet" },
  completed: { label: "Concluído", color: "green" },
  expired: { label: "Expirado", color: "gray" },
  revoked: { label: "Substituído", color: "gray" },
};

export function TeacherStudent() {
  const { id = "" } = useParams();
  const [params] = useSearchParams();
  const [tab, setTab] = useState<Tab>(params.get("sessao") ? "simulations" : "scenarios");
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ["teacher-students"],
    queryFn: () => api.get<{ items: TeacherStudentItem[] }>("/v1/teachers/me/students"),
  });
  const student = list.data?.items.find((s) => s.id === id);
  const [notes, setNotes] = useState("");
  const [classroom, setClassroom] = useState("");
  // biome-ignore lint/correctness/useExhaustiveDependencies: preenche o formulário só ao trocar de aluno.
  useEffect(() => {
    if (student) {
      setNotes(student.teacherNotes ?? "");
      setClassroom(student.classroomUrl ?? "");
    }
  }, [student?.id]);
  const patch = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.patch(`/v1/students/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["teacher-students"] }),
  });

  if (list.isLoading) return <Loading />;
  if (!student) return <Alert kind="risk">Aluno não encontrado.</Alert>;

  return (
    <>
      <PageHeader
        title={student.name}
        subtitle={`${student.planName} · ${student.scheduleLabel}`}
        actions={<Link to="/professor/alunos">Voltar aos alunos</Link>}
      />
      <div className="grid cols-3" style={{ marginBottom: 16 }}>
        <Card tight>
          <SelectInput
            label="Nível CEFR"
            value={student.cefrLevel ?? "A1"}
            onChange={(e) => patch.mutate({ cefrLevel: e.target.value as CefrLevel })}
            options={CEFR_LEVELS.map((l) => ({ value: l, label: l }))}
            hint={
              student.levelDeclaredLabel
                ? `Autodeclarado: ${student.levelDeclaredLabel}`
                : undefined
            }
          />
        </Card>
        <Card tight>
          <form
            className="stack"
            onSubmit={(e) => {
              e.preventDefault();
              patch.mutate({ classroomUrl: classroom || null });
            }}
          >
            <TextInput
              label="Turma no Google Classroom"
              type="url"
              placeholder="https://classroom.google.com/..."
              value={classroom}
              onChange={(e) => setClassroom(e.target.value)}
            />
            <Button type="submit" size="small" variant="secondary">
              Salvar link
            </Button>
          </form>
        </Card>
        <QuotaCard studentId={id} />
      </div>
      {patch.error ? <Alert kind="risk">{errorMessage(patch.error)}</Alert> : null}
      <Tabs
        value={tab}
        onChange={setTab}
        tabs={[
          { value: "scenarios", label: "Cenários" },
          { value: "simulations", label: "Simulações" },
          { value: "evolution", label: "Evolução" },
          { value: "notes", label: "Observações" },
        ]}
      />
      {tab === "scenarios" ? (
        <ScenariosTab studentId={id} level={(student.cefrLevel ?? "A1") as CefrLevel} />
      ) : null}
      {tab === "simulations" ? (
        <SimulationsTab studentId={id} openSession={params.get("sessao")} />
      ) : null}
      {tab === "evolution" ? <EvolutionTab studentId={id} /> : null}
      {tab === "notes" ? (
        <Card title="Observações privadas">
          <form
            className="stack"
            onSubmit={(e) => {
              e.preventDefault();
              patch.mutate({ teacherNotes: notes || null });
            }}
          >
            <TextArea
              label="Só você vê estas notas"
              rows={8}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
            <Button type="submit" loading={patch.isPending}>
              Salvar observações
            </Button>
          </form>
        </Card>
      ) : null}
    </>
  );
}

function QuotaCard({ studentId }: { studentId: string }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [minutes, setMinutes] = useState("10");
  const [reason, setReason] = useState("");
  const s = useQuery({
    queryKey: ["student-sessions", studentId],
    queryFn: () => api.get<{ quota: Quota | null }>(`/v1/students/${studentId}/sessions`),
  });
  const grant = useMutation({
    mutationFn: () =>
      api.post(`/v1/students/${studentId}/quota-grants`, { minutes: Number(minutes), reason }),
    onSuccess: () => {
      setOpen(false);
      qc.invalidateQueries({ queryKey: ["student-sessions", studentId] });
      qc.invalidateQueries({ queryKey: ["teacher-students"] });
    },
  });
  const q = s.data?.quota;
  return (
    <Card tight>
      <div className="label strong" style={{ color: "var(--navy)", fontSize: 14 }}>
        Minutos de IA no mês
      </div>
      <div className="stat">
        <div className="value">{q ? `${Math.floor(q.remainingMinutes)} min` : "sem cota"}</div>
        <div className="small muted">
          {q
            ? `usados ${Math.floor(q.usedMinutes)} de ${q.allowedMinutes}`
            : "a cota começa no primeiro pagamento"}
        </div>
      </div>
      <Button size="small" variant="secondary" onClick={() => setOpen(true)} disabled={!q}>
        Conceder minutos
      </Button>
      {open ? (
        <Modal title="Conceder minutos extras" onClose={() => setOpen(false)}>
          <div className="stack">
            <p className="small muted">
              Você pode conceder até 30 minutos extras por mês para cada aluno.
            </p>
            <TextInput
              label="Minutos"
              type="number"
              min={1}
              max={30}
              value={minutes}
              onChange={(e) => setMinutes(e.target.value)}
            />
            <TextInput label="Motivo" value={reason} onChange={(e) => setReason(e.target.value)} />
            {grant.error ? <Alert kind="risk">{errorMessage(grant.error)}</Alert> : null}
            <Button
              loading={grant.isPending}
              disabled={reason.trim().length < 3}
              onClick={() => grant.mutate()}
            >
              Conceder
            </Button>
          </div>
        </Modal>
      ) : null}
    </Card>
  );
}

function ScenariosTab({ studentId, level }: { studentId: string; level: CefrLevel }) {
  const qc = useQueryClient();
  const [mode, setMode] = useState<"list" | "library" | "generate">("list");
  const [editing, setEditing] = useState<Scenario | null>(null);
  const [draft, setDraft] = useState<ScenarioDraft | null>(null);
  const [goal, setGoal] = useState("");
  const [genLevel, setGenLevel] = useState<CefrLevel>(level);
  const [difficulty, setDifficulty] = useState("2");
  const [libLevel, setLibLevel] = useState<CefrLevel>(level);
  const mine = useQuery({
    queryKey: ["scenarios", studentId],
    queryFn: () => api.get<{ items: Scenario[] }>(`/v1/students/${studentId}/scenarios`),
  });
  const library = useQuery({
    queryKey: ["library", libLevel],
    queryFn: () => api.get<{ items: Scenario[] }>(`/v1/scenarios/library?level=${libLevel}`),
    enabled: mode === "library",
  });
  const copy = useMutation({
    mutationFn: (scenarioId: string) =>
      api.post(`/v1/students/${studentId}/scenarios`, { mode: "library", scenarioId }),
    onSuccess: () => {
      setMode("list");
      qc.invalidateQueries({ queryKey: ["scenarios", studentId] });
    },
  });
  const generate = useMutation({
    mutationFn: () =>
      api.post<{ draft: ScenarioDraft }>(`/v1/students/${studentId}/scenarios`, {
        mode: "generate",
        goalPt: goal,
        level: genLevel,
        difficulty: Number(difficulty),
      }),
    onSuccess: (r) => setDraft(r.draft),
  });
  const send = useMutation({
    mutationFn: (scenarioId: string) =>
      api.post(`/v1/students/${studentId}/simulations`, { scenarioId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["student-sessions", studentId] }),
  });

  return (
    <div className="stack">
      <div className="row">
        <Button
          variant={mode === "library" ? "primary" : "secondary"}
          onClick={() => setMode("library")}
        >
          Escolher da biblioteca
        </Button>
        <Button
          variant={mode === "generate" ? "primary" : "secondary"}
          onClick={() => setMode("generate")}
        >
          Gerar cenário
        </Button>
      </div>
      {send.isSuccess ? (
        <Alert kind="success">Simulação enviada por e-mail ao aluno. O link vale por 7 dias.</Alert>
      ) : null}
      {send.error ? <Alert kind="risk">{errorMessage(send.error)}</Alert> : null}
      {mode === "library" ? (
        <Card title="Biblioteca">
          <Chips
            value={libLevel}
            onChange={setLibLevel}
            options={CEFR_LEVELS.map((l) => ({ value: l, label: l }))}
          />
          {library.isLoading ? <Loading /> : null}
          <div className="stack" style={{ marginTop: 12 }}>
            {library.data?.items.map((s) => (
              <div key={s.id} className="list-item">
                <div>
                  <strong>{s.title}</strong>
                  <div className="small muted">{s.aiPersona}</div>
                </div>
                <Button
                  size="small"
                  loading={copy.isPending && copy.variables === s.id}
                  onClick={() => copy.mutate(s.id)}
                >
                  Usar
                </Button>
              </div>
            ))}
          </div>
        </Card>
      ) : null}
      {mode === "generate" && !draft ? (
        <Card title="Gerar cenário com IA">
          <div className="stack">
            <TextArea
              label="Objetivo em português"
              placeholder="Praticar pedir comida e perguntar ingredientes"
              value={goal}
              onChange={(e) => setGoal(e.target.value)}
            />
            <div className="grid cols-2">
              <SelectInput
                label="Nível"
                value={genLevel}
                onChange={(e) => setGenLevel(e.target.value as CefrLevel)}
                options={CEFR_LEVELS.map((l) => ({ value: l, label: l }))}
              />
              <SelectInput
                label="Dificuldade"
                value={difficulty}
                onChange={(e) => setDifficulty(e.target.value)}
                options={[1, 2, 3, 4, 5].map((d) => ({ value: String(d), label: String(d) }))}
              />
            </div>
            {generate.error ? <Alert kind="risk">{errorMessage(generate.error)}</Alert> : null}
            <Button
              loading={generate.isPending}
              disabled={goal.trim().length < 5}
              onClick={() => generate.mutate()}
            >
              Gerar cenário
            </Button>
          </div>
        </Card>
      ) : null}
      {draft ? (
        <ScenarioEditor
          studentId={studentId}
          initial={draft}
          onDone={() => {
            setDraft(null);
            setMode("list");
            qc.invalidateQueries({ queryKey: ["scenarios", studentId] });
          }}
        />
      ) : null}
      {editing ? (
        <ScenarioEditor
          studentId={studentId}
          scenarioId={editing.id}
          initial={editing}
          onDone={() => {
            setEditing(null);
            qc.invalidateQueries({ queryKey: ["scenarios", studentId] });
          }}
        />
      ) : null}
      <Card title="Cenários do aluno">
        {mine.data?.items.length === 0 ? (
          <Empty>Escolha da biblioteca ou gere um cenário para enviar a primeira simulação.</Empty>
        ) : null}
        <div className="stack">
          {mine.data?.items.map((s) => (
            <div key={s.id} className="card tight">
              <div className="row between">
                <div>
                  <strong>{s.title}</strong> <Badge color="blue">{s.level}</Badge>{" "}
                  <Badge color="gray">
                    {s.source === "library"
                      ? "biblioteca"
                      : s.source === "generated"
                        ? "gerado"
                        : "próprio"}
                  </Badge>
                  <div className="small muted">{s.objectives.join(" · ")}</div>
                  <div className="small muted">
                    Vocabulário: {s.targetVocabulary.map((v) => v.word).join(", ")}
                  </div>
                </div>
                <div className="row">
                  <Button size="small" variant="secondary" onClick={() => setEditing(s)}>
                    Editar
                  </Button>
                  <Button
                    size="small"
                    loading={send.isPending && send.variables === s.id}
                    onClick={() => send.mutate(s.id)}
                  >
                    Enviar simulação
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

function SimulationsTab({
  studentId,
  openSession,
}: {
  studentId: string;
  openSession: string | null;
}) {
  const [reportId, setReportId] = useState<string | null>(openSession);
  const q = useQuery({
    queryKey: ["student-sessions", studentId],
    queryFn: () =>
      api.get<{ links: SimulationLinkSummary[]; sessions: SessionSummary[]; quota: Quota | null }>(
        `/v1/students/${studentId}/sessions`,
      ),
    refetchInterval: 30_000,
  });
  if (q.isLoading) return <Loading />;
  const d = q.data;
  return (
    <div className="stack">
      <Card title="Links enviados">
        {d?.links.length === 0 ? <Empty>Nenhuma simulação enviada.</Empty> : null}
        <div className="stack">
          {d?.links.map((l) => (
            <div key={l.id} className="row between">
              <span>
                {l.title} <span className="small muted">· enviado {dateBR(l.createdAt)}</span>
              </span>
              <Badge color={LINK_STATUS[l.status].color}>{LINK_STATUS[l.status].label}</Badge>
            </div>
          ))}
        </div>
      </Card>
      <Card title="Histórico de simulações">
        {d?.sessions.length === 0 ? <Empty>O aluno ainda não fez simulações.</Empty> : null}
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Data</th>
                <th>Duração</th>
                <th className="num">Nota</th>
                <th>Objetivos</th>
                <th>Situação</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {d?.sessions.map((s) => (
                <tr key={s.id}>
                  <td>{dateTimeBR(s.startedAt)}</td>
                  <td>{Math.round(s.activeSeconds / 60)} min</td>
                  <td className="num">{s.scores ? score(s.scores.overall) : "..."}</td>
                  <td>{s.objectivesTotal ? `${s.objectivesDone} de ${s.objectivesTotal}` : ""}</td>
                  <td>
                    {s.flag ? (
                      <Badge color="red">Atenção</Badge>
                    ) : s.status === "ended" ? (
                      "Concluída"
                    ) : (
                      "Em andamento"
                    )}
                  </td>
                  <td>
                    <Button size="small" variant="ghost" onClick={() => setReportId(s.id)}>
                      Relatório
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
      {reportId ? <ReportModal sessionId={reportId} onClose={() => setReportId(null)} /> : null}
    </div>
  );
}

function ReportModal({ sessionId, onClose }: { sessionId: string; onClose: () => void }) {
  const q = useQuery({
    queryKey: ["session-report", sessionId],
    queryFn: () =>
      api.get<{
        session: {
          flag: string | null;
          activeSeconds: number;
          p50Ms: number | null;
          endReason: string | null;
        };
        scenario: { title: string };
        report: FullReport | null;
        transcript: { seq: number; role: string; text: string }[];
      }>(`/v1/sessions/${sessionId}/report`),
  });
  return (
    <Modal title={q.data?.scenario.title ?? "Relatório"} onClose={onClose}>
      {q.isLoading ? <Loading /> : null}
      {q.error ? <Alert kind="risk">{errorMessage(q.error)}</Alert> : null}
      {q.data ? (
        <div className="stack">
          {q.data.session.flag ? (
            <Alert kind="risk" title="Sessão marcada">
              {q.data.session.flag === "wellbeing"
                ? "Possível sinal de sofrimento. Converse com cuidado e, se for menor, com o responsável."
                : "Conteúdo sensível na conversa."}
            </Alert>
          ) : null}
          {!q.data.report ? (
            <Alert kind="note">
              O relatório ainda está sendo preparado ou a conversa foi curta demais.
            </Alert>
          ) : null}
          {q.data.report ? (
            <>
              <div className="grid cols-2">
                <ScoreBar label="Geral" value={q.data.report.overall} />
                <ScoreBar label="Fluência" value={q.data.report.fluency} />
                <ScoreBar label="Vocabulário" value={q.data.report.vocabulary} />
                <ScoreBar label="Gramática" value={q.data.report.grammar} />
                <ScoreBar label="Compreensão" value={q.data.report.comprehension} />
              </div>
              <p>{q.data.report.summaryPt}</p>
              <h3>Objetivos</h3>
              {q.data.report.objectives.map((o) => (
                <div key={o.objective} className="small">
                  {o.achieved ? "Cumprido" : "Não cumprido"}: <strong>{o.objective}</strong>
                  <div className="muted">{o.evidence}</div>
                </div>
              ))}
              {q.data.report.errors.length ? <h3>Correções</h3> : null}
              {q.data.report.errors.map((e, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: lista fixa do relatório, sem reordenação.
                <div key={`${e.said}-${i}`} className="small">
                  <span style={{ textDecoration: "line-through" }}>{e.said}</span>{" "}
                  <strong>{e.better}</strong>
                  <div className="muted">{e.why}</div>
                </div>
              ))}
            </>
          ) : null}
          <h3>Transcrição</h3>
          <div className="transcript">
            {q.data.transcript.map((t) => (
              <div key={t.seq} className={`bubble ${t.role === "student" ? "student" : "ai"}`}>
                {t.text.replace(/<\/?pt>/g, "")}
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </Modal>
  );
}

function EvolutionTab({ studentId }: { studentId: string }) {
  const q = useQuery({
    queryKey: ["student-sessions", studentId],
    queryFn: () => api.get<{ sessions: SessionSummary[] }>(`/v1/students/${studentId}/sessions`),
  });
  const scored = (q.data?.sessions ?? []).filter((s) => s.scores).reverse();
  if (q.isLoading) return <Loading />;
  if (scored.length === 0)
    return <Empty>A evolução aparece depois da primeira simulação com relatório.</Empty>;
  const labels = scored.map((s) => dateBR(s.startedAt).slice(0, 5));
  const pick = (k: "fluency" | "vocabulary" | "grammar" | "comprehension" | "average") =>
    scored.map((s) => s.scores?.[k] ?? null);
  return (
    <Card title="Evolução por simulação">
      <LineChart
        labels={labels}
        series={[
          { label: "Fluência", color: "#1F4FD1", values: pick("fluency") },
          { label: "Vocabulário", color: "#0E9F6E", values: pick("vocabulary") },
          { label: "Gramática", color: "#E0353B", values: pick("grammar") },
          { label: "Compreensão", color: "#F2B01E", values: pick("comprehension") },
          { label: "Média", color: "#0B1B3D", values: pick("average") },
        ]}
      />
    </Card>
  );
}
